import React from "react";
import {
  Card,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  CircularProgress
} from "@mui/material";
import { ReactComponent as EditIcon } from "../../assets/edit.svg";
import TotalWeightUpdate from "../Modals/TotalWeightUpdate";
import useThemeColor from "../../utils/useTheme";
import { useTheme } from "@emotion/react";
import useIsMobile from "./useMobile";

const CircularGauge = ({
  diaryId,
  levelId,
  isCalories = false,
  strokeColor,
  title,
  value,
  nonEditable = false,
  levelName,
  maxValue,
  dayName,
  openWeightModal,
  setIsRefreshNeeded,
  setOpenWeightModal,
  loading,
  widthX = 350,
  heightX = 250
}) => {
  const isLandscape = useIsMobile();
  const isLaptop = useMediaQuery("(min-width: 900px) and (max-width:1300px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Dynamic sizing based on level and device
  let width = isLandscape ? 240 : widthX;
  let height = isLandscape ? 160 : heightX;
  let strokeWidth = isMobile || isTablet ? 15 : 20;
  let fontSize = {
    title: isMobile || isTablet ? "14px" : "16px",
    value: isMobile || isTablet ? "34px" : "44px",
    subtitle: "14px",
    outOf: "14px"
  };

  // Level 2 adjustments
  if ((isMobile || isLaptop) && levelName === "level2") {
    width = 190;
    height = 140;
    strokeWidth = 13;
    fontSize = {
      title: "11px",
      value: "22px",
      subtitle: "12px",
      outOf: "10px"
    };
  }

  // Level 3 adjustments
  if (levelName === "level3") {
    width = isMobile || isTablet ? 150 : isLaptop ? 235 : 260;
    height = isMobile || isTablet ? 115 : isLaptop ? 170 : 190;
    strokeWidth = isMobile || isTablet ? 10 : 15;
    fontSize = {
      title: isMobile || isTablet ? "10px" : "12px",
      value: isMobile || isTablet ? "22px" : "39px",
      subtitle: isMobile || isTablet ? "10px" : "14px",
      outOf: isMobile || isTablet ? "9px" : "14px"
    };
  }

  const styles = useThemeColor();

  // Calculate the percentage based on value and maxValue
  const percentage = (value / maxValue) * 100;

  // Calculate radius and center based on dynamic width and height
  const radius = (width - 50) / 2;
  const centerX = width / 2;
  const centerY = width / 2;

  // Define start and end angles for a semicircle
  const startAngle = -200;
  const endAngle = 20;

  // Calculate the angle for the current value
  const valueAngle = Math.min(
    startAngle + (endAngle - startAngle) * (percentage / 100),
    endAngle
  );

  // Create the SVG arc path for the background and progress
  const createArcPath = (start, end) => {
    const rad = (angle) => (angle * Math.PI) / 180;

    const startPoint = {
      x: centerX + radius * Math.cos(rad(start)),
      y: centerY + radius * Math.sin(rad(start))
    };

    const endPoint = {
      x: centerX + radius * Math.cos(rad(end)),
      y: centerY + radius * Math.sin(rad(end))
    };

    const largeArcFlag = Math.abs(end - start) > 180 ? 1 : 0;

    return `
      M ${startPoint.x} ${startPoint.y}
      A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endPoint.x} ${endPoint.y}
    `;
  };

  const backgroundArc = createArcPath(startAngle, endAngle);
  const progressArc = createArcPath(startAngle, valueAngle);
  if (loading || maxValue === undefined || maxValue === null) {
    return (
      <Card
        sx={{
          backgroundColor: styles.diaryTracker,
          boxShadow: "none !important",
          padding: levelName === "level3" ? 2 : 4,
          width,
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <CircularProgress
          size={isMobile ? 40 : 60}
          color="primary"
          sx={{ color: strokeColor || styles.color }}
        />
      </Card>
    );
  }
  return (
    <Card
      sx={{
        backgroundColor: styles.diaryTracker,
        boxShadow: "none !important",
        padding: levelName === "level3" ? 2 : 4,
        width,
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Box position="relative">
        <svg width={width} height={height}>
          <path
            d={backgroundArc}
            fill="none"
            stroke={styles.trackerBackground}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <path
            d={progressArc}
            fill="none"
            stroke={
              isCalories
                ? value > maxValue + 100
                  ? "#F6541C" // 🔴 Red if >100 over
                  : Math.abs(value - maxValue) <= 100
                  ? "#03B55C" // 🟢 Green if within ±100
                  : strokeColor || "#FFC107" // 🟡 Yellow default
                : value >= maxValue
                ? "#03B55C"
                : strokeColor
            }
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{
              transition: "stroke-dashoffset 0.5s ease"
            }}
          />
        </svg>

        <Box
          position="absolute"
          top="70%"
          left="50%"
          sx={{
            transform: "translate(-50%, -50%)",
            textAlign: "center"
          }}
        >
          <Typography
            fontSize={fontSize.title}
            color={styles.color}
            sx={{ opacity: 0.75 }}
          >
            {title}
          </Typography>
          <Typography
            fontSize={fontSize.value}
            color={styles.color}
            fontWeight="bold"
          >
            {value}
            {isCalories ? "" : "g"}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            gap="5px"
            justifyContent="center"
          >
            <Typography fontSize={fontSize.subtitle} color="#8E8E8E">
              out of
            </Typography>
            <Typography fontSize={fontSize.outOf} color="#368AD0">
              {maxValue}
              {isCalories ? "" : "g"}
            </Typography>
            {!nonEditable && (
              <IconButton
                sx={{
                  borderRadius: "50%",
                  height:
                    levelName === "level3"
                      ? isMobile || isLaptop
                        ? 16
                        : 18
                      : (isMobile || isLaptop) && levelName === "level2"
                      ? 18
                      : 20,
                  width:
                    levelName === "level3"
                      ? isMobile || isLaptop
                        ? 16
                        : 18
                      : (isMobile || isLaptop) && levelName === "level2"
                      ? 18
                      : 20,
                  padding: "0",
                  svg: {
                    path: {
                      fill: "#368AD0"
                    }
                  }
                }}
                onClick={() => {
                  setOpenWeightModal(title);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        <TotalWeightUpdate
          levelName={levelName}
          title={title}
          setIsRefreshNeeded={setIsRefreshNeeded}
          diaryId={diaryId}
          levelId={levelId}
          maxValue={maxValue}
          openWeightModal={openWeightModal}
          setOpenWeightModal={setOpenWeightModal}
          dayName={dayName}
        />
      </Box>
    </Card>
  );
};

export default CircularGauge;
